import { useCallback, useEffect, useState } from "react";
import {
  Activity,
  Copy,
  Link2,
  Loader2,
  Plug,
  RadioTower,
  RefreshCw,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, apiError, fmtDay, fmtWhen } from "@/lib/api";
import { ApiGuideButton } from "@/components/ApiGuideButton";

const STATE = {
  live: { word: "Live", led: "var(--healthy)", said: "readings are arriving" },
  failing: { word: "Last test failed", led: "var(--critical)", said: "the wire did not answer" },
  stale: { word: "Gone quiet", led: "var(--at-risk)", said: "nothing has arrived lately" },
  waiting: { word: "Waiting", led: "var(--watch)", said: "switched on, nothing posted yet" },
  off: { word: "Not connected", led: "var(--muted-foreground)", said: "filled by hand" },
};

/** A connection's light — green alive, amber quiet, red refused, grey not connected. */
const Led = ({ state, testId }) => {
  const tone = STATE[state] || STATE.off;
  return (
    <span
      className={`led ${state === "off" ? "led-still" : ""}`}
      style={{ "--led": tone.led }}
      data-testid={testId}
      data-state={state}
      title={`${tone.word} — ${tone.said}`}
    />
  );
};

const Step = ({ n, title, children }) => (
  <li className="flex gap-3">
    <span className="font-data flex h-6 w-6 shrink-0 items-center justify-center border border-primary text-[11px] font-bold text-primary">
      {n}
    </span>
    <span className="min-w-0 flex-1">
      <b className="block text-[12px] font-semibold">{title}</b>
      <span className="block text-[11px] leading-relaxed text-muted-foreground">{children}</span>
    </span>
  </li>
);

/**
 * Connecting one usecase to the plant's own system.
 *
 * A usecase is opened to automated entry by a super-admin, one at a time. The light beside it is
 * read from the readings themselves and from the interval the super-admins have set — so this can
 * never claim "live" while nothing is coming in. Test Connection knocks on the wire and reports
 * what came back without saving a figure.
 */
export const ApiConnections = ({ plants = "" }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [testing, setTesting] = useState("");
  const [tests, setTests] = useState({});
  const [urls, setUrls] = useState({});
  const [minutes, setMinutes] = useState("");
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/compliance/ingest`;

  const load = useCallback(() => {
    api
      .get("/compliance/connections", { params: { plants } })
      .then((r) => {
        setData(r.data);
        setMinutes(String(r.data.refresh_minutes));
        setUrls(
          Object.fromEntries(r.data.rows.map((row) => [row.project_id, row.api_source_url || ""])),
        );
      })
      .catch((e) => toast.error(apiError(e)));
  }, [plants]);

  useEffect(load, [load]);

  const flip = async (row, on) => {
    setBusy(row.project_id);
    try {
      await api.patch(`/compliance/profiles/${row.project_id}`, { api_enabled: on });
      toast.success(
        on
          ? `${row.code} is open to automated entry. Its system may post from now.`
          : `${row.code} is closed to automated entry. Readings already posted are kept.`,
      );
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const saveUrl = async (row) => {
    setBusy(row.project_id);
    try {
      await api.patch(`/compliance/profiles/${row.project_id}`, {
        api_source_url: urls[row.project_id] || "",
      });
      toast.success(
        urls[row.project_id]
          ? `${row.code} will be read from that address every ${data.refresh_minutes} minute(s).`
          : `${row.code} goes back to waiting for its system to post.`,
      );
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const test = async (row) => {
    setTesting(row.project_id);
    try {
      const { data: out } = await api.post(`/compliance/connections/${row.project_id}/test`);
      setTests((t) => ({ ...t, [row.project_id]: out.test }));
      (out.test.ok ? toast.success : toast.error)(`${row.code}: ${out.test.said}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTesting("");
    }
  };

  const saveMinutes = async () => {
    setBusy("interval");
    try {
      const { data: out } = await api.put("/settings/compliance-api", {
        refresh_minutes: Number(minutes),
      });
      toast.success(
        `A connected usecase will be read every ${out.settings.refresh_minutes} minute(s), and its light turns amber if nothing has come in by then.`,
      );
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const copy = (row) => {
    const sample = `curl -X POST "${base}" \\
  -H "X-API-Key: <the key SDTD gave your unit>" \\
  -H "Content-Type: application/json" \\
  -d '{"readings": [
        {"code": "${row.code}", "date": "YYYY-MM-DD", "pct": 92,
         "kpi_value": 556.2, "impact_lakh": 19.0, "remark": "shift average"}
      ]}'`;
    navigator.clipboard?.writeText(sample);
    toast.success(`The format for ${row.code} is copied — send it to your system's team.`);
  };

  if (!data)
    return (
      <div className="flex h-24 items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );

  const t = data.totals;

  return (
    <div data-testid="api-connections">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <ApiGuideButton testId="api-guide-download-connections" />
        <span className="text-[11px] text-muted-foreground">
          Share this note with a unit&apos;s IT team — it explains both ways to connect, with
          ready-to-run examples.
        </span>
      </div>
      <div className="grid gap-px bg-border sm:grid-cols-4">
        {[
          ["Connected usecases", t.connected, "connected"],
          ["Reading as it should", t.live, "live"],
          ["Quiet or refused", t.stale, "stale"],
          ["Switched on, waiting", t.waiting, "waiting"],
        ].map(([label, value, key]) => (
          <div key={key} className="bg-card p-3" data-testid={`api-total-${key}`}>
            <div className="label-caps">{label}</div>
            <div className="font-data mt-1 text-xl font-black tabular-nums">{value}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap items-end justify-between gap-4 border border-border bg-card p-4">
        <div>
          <div className="label-caps flex items-center gap-1.5">
            <RadioTower className="h-3 w-3" /> How often the portal reads a connected system
          </div>
          <p className="mt-1 max-w-xl text-[11px] leading-relaxed text-muted-foreground">
            A usecase with an address below is read on this interval, and the light beside it turns
            amber the moment nothing has come in within it. Five minutes is the portal&apos;s
            setting; a system that reports once a shift is better given a longer one.
          </p>
        </div>
        {data.can_switch ? (
          <div className="flex items-end gap-2">
            <label className="block">
              <span className="label-caps block">Refresh every (minutes)</span>
              <Input
                type="number"
                min={1}
                max={1440}
                className="font-data mt-1 h-11 w-[120px] rounded-none"
                data-testid="api-refresh-minutes"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
              />
            </label>
            <button
              type="button"
              disabled={busy === "interval" || Number(minutes) === data.refresh_minutes}
              data-testid="api-refresh-save"
              onClick={saveMinutes}
              className="flex h-11 items-center gap-2 bg-primary px-4 font-display text-[10px] font-bold uppercase tracking-widest text-primary-foreground transition-opacity duration-200 hover:opacity-90 disabled:opacity-40"
            >
              {busy === "interval" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Set it
            </button>
          </div>
        ) : (
          <p className="font-data text-[11px] text-muted-foreground" data-testid="api-refresh-read">
            Every {data.refresh_minutes} minute(s) — a super-admin sets this.
          </p>
        )}
      </div>

      <div className="mt-5 grid gap-px bg-border lg:grid-cols-[1.1fr_1fr]">
        <div className="bg-card p-4">
          <div className="label-caps flex items-center gap-1.5">
            <Plug className="h-3 w-3" /> How a plant team connects its system — step by step
          </div>
          <ol className="mt-3 space-y-3" data-testid="api-explainer">
            <Step n="1" title="Put the usecase on this screen first">
              A reading can only be posted against a usecase that is already here, with its KPI,
              target, baseline and target impact declared. The portal will never invent a KPI it
              has not been told about.
            </Step>
            <Step n="2" title="Ask SDTD to switch this usecase on">
              The toggle beside each usecase below is a super-admin&apos;s. Until it is on, a post
              for that usecase is refused with that very reason in the reply — nothing is lost.
            </Step>
            <Step n="3" title="Then choose which way the data moves">
              Either your system <b className="text-foreground">posts</b> to the portal with your
              unit&apos;s key, or you give SDTD an address and the portal{" "}
              <b className="text-foreground">reads</b> it every few minutes. Both end in the same
              one record.
            </Step>
            <Step n="4" title="Ask SDTD for your unit's key">
              One key per unit, issued below and shown once. Your system sends it in the{" "}
              <b className="text-foreground">X-API-Key</b> header. A key for BSL can only ever
              write BSL&apos;s readings.
            </Step>
            <Step n="5" title="Press Test connection before you go live">
              It knocks on the wire and tells you whether it answered, how long it took and whether
              the reading reads well — and it saves nothing while you are still setting up.
            </Step>
            <Step n="6" title="Watch the light">
              Green means readings are arriving within the interval above. Amber means the wire has
              gone quiet. Red means the last test was refused. Nothing is ever doubled — a day sent
              twice corrects the day already there.
            </Step>
          </ol>
        </div>

        <div className="bg-card p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="label-caps flex items-center gap-1.5">
              <Activity className="h-3 w-3" /> Live sync, usecase by usecase
            </div>
            <button
              type="button"
              data-testid="api-connections-refresh"
              onClick={load}
              className="flex items-center gap-1 font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground transition-colors duration-200 hover:text-foreground"
            >
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
          </div>
          <div className="mt-2 divide-y divide-border border border-border">
            {data.rows.map((row) => {
              const tone = STATE[row.state] || STATE.off;
              const result = tests[row.project_id] || row.last_test;
              return (
                <div
                  key={row.project_id}
                  className="px-3 py-2.5"
                  data-testid={`api-connection-${row.code}`}
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <Led state={row.state} testId={`api-led-${row.code}`} />
                    <span className="min-w-0 flex-1">
                      <span className="font-data block text-[10px] text-muted-foreground">
                        {row.plant} · {row.code} · {row.kpi_name || "KPI not named"}
                      </span>
                      <span className="block truncate text-[12px] font-semibold">{row.title}</span>
                      <span
                        className="font-data mt-0.5 block text-[10px]"
                        style={{ color: `hsl(${tone.led})` }}
                        data-testid={`api-state-${row.code}`}
                      >
                        {tone.word} — {tone.said}
                        {row.last_api_day
                          ? ` · last ${fmtDay(row.last_api_day)} · ${row.api_days} day(s) by API`
                          : ""}
                        {row.minutes_since !== null && row.minutes_since !== undefined
                          ? ` · ${row.minutes_since} min ago`
                          : ""}
                        {row.by_hand_days ? ` · ${row.by_hand_days} day(s) by hand` : ""}
                      </span>
                    </span>
                    <button
                      type="button"
                      data-testid={`api-test-${row.code}`}
                      disabled={testing === row.project_id}
                      onClick={() => test(row)}
                      className="flex items-center gap-1 border border-border px-2 py-1.5 font-display text-[9.5px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted disabled:opacity-40"
                    >
                      {testing === row.project_id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <RadioTower className="h-3 w-3" />
                      )}
                      Test connection
                    </button>
                    <button
                      type="button"
                      data-testid={`api-copy-${row.code}`}
                      onClick={() => copy(row)}
                      className="flex items-center gap-1 border border-border px-2 py-1.5 font-display text-[9.5px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted"
                    >
                      <Copy className="h-3 w-3" /> Copy the format
                    </button>
                    <Switch
                      data-testid={`api-switch-${row.code}`}
                      disabled={!data.can_switch || busy === row.project_id}
                      checked={row.api_enabled}
                      onCheckedChange={(v) => flip(row, v)}
                    />
                  </div>

                  {data.can_switch && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Link2 className="h-3 w-3 shrink-0 text-muted-foreground" />
                      <Input
                        className="font-data h-9 flex-1 rounded-none text-[11px]"
                        placeholder="https://plant-system/compliance/latest — leave blank if the unit posts to us"
                        data-testid={`api-source-${row.code}`}
                        value={urls[row.project_id] ?? ""}
                        onChange={(e) =>
                          setUrls((u) => ({ ...u, [row.project_id]: e.target.value }))
                        }
                      />
                      <button
                        type="button"
                        data-testid={`api-source-save-${row.code}`}
                        disabled={
                          busy === row.project_id ||
                          (urls[row.project_id] ?? "") === (row.api_source_url || "")
                        }
                        onClick={() => saveUrl(row)}
                        className="flex h-9 items-center gap-1 border border-border px-2 font-display text-[9.5px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted disabled:opacity-30"
                      >
                        <Save className="h-3 w-3" /> Save
                      </button>
                    </div>
                  )}

                  {result && (
                    <div
                      className="mt-2 border border-border bg-muted/30 p-2"
                      data-testid={`api-test-result-${row.code}`}
                    >
                      <span className="font-data block text-[10.5px]">
                        <b style={{ color: `hsl(${result.ok ? "var(--healthy)" : "var(--critical)"})` }}>
                          {result.ok ? "Answered" : "No answer"}
                        </b>
                        {result.http_status ? ` · HTTP ${result.http_status}` : ""}
                        {result.ms !== null && result.ms !== undefined ? ` · ${result.ms} ms` : ""}
                        {result.at ? ` · tested ${fmtWhen(result.at)}` : ""}
                      </span>
                      <span className="mt-0.5 block text-[10.5px] text-muted-foreground">
                        {result.said}
                      </span>
                      {result.reading && (
                        <span
                          className="font-data mt-1 block text-[10.5px]"
                          data-testid={`api-test-reading-${row.code}`}
                        >
                          Reading seen: {result.reading.date}
                          {result.reading.pct !== null && result.reading.pct !== undefined
                            ? ` · ${result.reading.pct}%`
                            : ""}
                          {result.reading.kpi_value !== null &&
                          result.reading.kpi_value !== undefined
                            ? ` · KPI ${result.reading.kpi_value}`
                            : ""}
                          {result.reading.impact_lakh !== null &&
                          result.reading.impact_lakh !== undefined
                            ? ` · ₹${result.reading.impact_lakh} L`
                            : ""}
                          <span className="ml-1 text-muted-foreground">
                            — shown only, nothing was saved
                          </span>
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {!data.rows.length && (
              <p className="px-3 py-4 text-[11px] text-muted-foreground">
                Nothing is on this screen yet, so there is nothing to connect.
              </p>
            )}
          </div>
          {!data.can_switch && (
            <p className="mt-2 text-[11px] text-muted-foreground" data-testid="api-switch-locked">
              Switching a usecase on, and giving the portal an address to read, are a
              super-admin&apos;s call — ask SDTD and it is done in a moment. Testing the
              connection is yours to use.
            </p>
          )}
          <p className="font-data mt-3 break-all border border-border bg-muted/40 p-2 text-[10px]">
            POST {base}
          </p>
          {!!data.rows.find((r) => r.keys?.length) && (
            <p className="mt-2 text-[10.5px] text-muted-foreground">
              Keys in use for these units:{" "}
              {[
                ...new Set(
                  data.rows.flatMap((r) =>
                    (r.keys || []).map(
                      (k) =>
                        `${k.name} (${k.plant || "every unit"}, ${k.calls} call(s)${
                          k.last_used_at ? `, last used ${fmtWhen(k.last_used_at)}` : ""
                        })`,
                    ),
                  ),
                ),
              ].join(" · ")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
